/**
 * The help library (TMMAPP-0142 U2 item 12; TMMAPI-0181 U4, MM-D-147): how-to articles, free for
 * everyone and never AI. Wire types from `src/api/generated/`, normalized into the module's domain
 * types.
 *
 * - **Sections:** `{key, articleCount}`; the app labels each key from its catalog.
 * - **Articles:** the list (in the person's language, English filling a gap; by relevance with `q`,
 *   one section with `section`), and one article as the library's Markdown subset.
 * - **Images** need the bearer token, so they're read through the API client as a blob. Only a path
 *   under `/v1/me/help/images/` is ever fetched: the served body's path, used as is (today
 *   `/v1/me/help/images/<name>`, after TMMAPI-0182 U1 `/v1/me/help/images/<locale>/<name>`).
 */
import { download, request } from './client';
import type {
  HelpArticle as WireArticle,
  HelpArticleSummary as WireArticleSummary,
  HelpSection as WireSection,
} from './generated';

export interface HelpSection {
  key: string;
  articleCount: number;
}

export interface HelpArticleSummary {
  id: string;
  title: string;
  /** Plain text: the article's opening, or with a query its best-matching part. */
  excerpt: string;
  /** The section's key; null when not served. */
  section: string | null;
  order: number | null;
  related: string[];
}

export interface HelpArticle {
  id: string;
  title: string;
  /** The library's Markdown subset, `{productName}` already substituted. */
  body: string;
  /** The language served: the person's when the library has it, else `en`. */
  locale: string;
  section: string | null;
  related: string[];
  /** `YYYY-MM-DD`; null when not served. */
  updated: string | null;
}

/** The only image paths the app fetches: the library's own route, through the API client. */
export const HELP_IMAGE_PREFIX = '/v1/me/help/images/';

/** A served image path the app may fetch: under the help images route, with no way out of it. */
export function isHelpImagePath(src: string): boolean {
  if (!src.startsWith(HELP_IMAGE_PREFIX)) return false;
  const rest = src.slice(HELP_IMAGE_PREFIX.length);
  // No query, fragment, backslash, percent-encoding (an encoded `..` or `/` can't leave the route),
  // whitespace or control character (M10).
  // eslint-disable-next-line no-control-regex -- control characters are exactly what it refuses
  const refused = /[?#\\%\s\x00-\x1f]/;
  return rest.length > 0 && !refused.test(rest) && !rest.split('/').some((part) => part === '..');
}

export function normalizeSection(w: WireSection): HelpSection {
  return { key: w.key, articleCount: Math.max(0, w.articleCount) };
}

export function normalizeArticleSummary(w: WireArticleSummary): HelpArticleSummary {
  return {
    id: w.id,
    title: w.title,
    excerpt: w.excerpt,
    section: w.section ?? null,
    order: w.order ?? null,
    related: [...w.related],
  };
}

export function normalizeArticle(w: WireArticle): HelpArticle {
  return {
    id: w.id,
    title: w.title,
    body: w.body,
    locale: w.locale,
    section: w.section ?? null,
    related: [...w.related],
    updated: w.updated ?? null,
  };
}

const BASE = '/v1/me/help';

export const helpApi = {
  /** GET /v1/me/help/sections — ordered by key; the app orders them by the library's plan. */
  async sections(): Promise<HelpSection[]> {
    return (await request<WireSection[]>('GET', `${BASE}/sections`)).map(normalizeSection);
  },
  /** GET /v1/me/help/articles?q=&section= — either may be left out. */
  async articles(
    filter: { q?: string | undefined; section?: string | undefined } = {},
  ): Promise<HelpArticleSummary[]> {
    const query: Record<string, string> = {};
    if (filter.q !== undefined && filter.q.trim().length > 0) query.q = filter.q.trim();
    if (filter.section !== undefined) query.section = filter.section;
    const wire = await request<WireArticleSummary[]>('GET', `${BASE}/articles`, {
      ...(Object.keys(query).length > 0 ? { query } : {}),
    });
    return wire.map(normalizeArticleSummary);
  },
  /** GET /v1/me/help/articles/{id} — `404 HELP_ARTICLE_NOT_FOUND` when it isn't there. */
  async article(id: string): Promise<HelpArticle> {
    return normalizeArticle(
      await request<WireArticle>('GET', `${BASE}/articles/${encodeURIComponent(id)}`),
    );
  },
  /**
   * An article's image by its served path (bearer only, so a Blob). A path outside the help images
   * route is never fetched: it rejects without a request. `signal` aborts it (M8).
   */
  async image(src: string, signal?: AbortSignal): Promise<Blob> {
    if (!isHelpImagePath(src)) throw new Error('not a help image path');
    const { blob } = await download(src, {
      accept: 'image/*',
      ...(signal !== undefined ? { signal } : {}),
    });
    return blob;
  },
};
